import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, User, ShoppingBag, Gift, Home as HomeIcon, X, ChevronDown, ChevronRight, Heart, LayoutDashboard, Package, Shirt, CalendarDays, RotateCcw, Ruler, LogOut, Boxes, ClipboardList } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useCart } from "@/lib/cart";
import { api } from "@/lib/api";
import NotificationBell from "@/components/NotificationBell";

const Logo = () => (
  <Link to="/" data-testid="header-logo" className="flex items-center group">
    <img src="/images/logo.jpg" alt="SquireCouture" className="h-12 w-auto object-contain" />
  </Link>
);

const MasterSearch = ({ open, setOpen }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  const run = async (val) => {
    setQ(val);
    if (!val.trim()) { setResults(null); return; }
    setLoading(true);
    try { const { data } = await api.get(`/search?q=${encodeURIComponent(val)}`); setResults(data); } catch {}
    setLoading(false);
  };
  const close = () => { setOpen(false); setQ(""); setResults(null); };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[80] bg-black/40 backdrop-blur-sm" data-testid="master-search-overlay" onClick={close}>
      <div className="bg-white max-w-3xl mx-auto mt-24 p-8 sq-fade-up" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-4 border-b border-black pb-4">
          <Search size={20} />
          <input
            data-testid="master-search-input"
            autoFocus
            placeholder="Search by id, name, fabric, colour, price…"
            className="flex-1 bg-transparent outline-none text-lg font-display"
            value={q}
            onChange={(e) => run(e.target.value)}
          />
          <button data-testid="master-search-close" onClick={close}><X /></button>
        </div>
        <div className="max-h-[60vh] overflow-y-auto sq-scroll mt-6 space-y-6">
          {loading && <div className="sq-eyebrow">Searching…</div>}
          {results?.products?.length > 0 && (
            <div>
              <div className="sq-eyebrow mb-3">Products</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {results.products.map((p) => (
                  <button
                    key={p.id}
                    data-testid={`search-product-${p.id}`}
                    onClick={() => { close(); nav(`/product/${p.id}`); }}
                    className="flex items-center gap-3 p-2 border border-transparent hover:border-black transition-colors text-left"
                  >
                    <img src={p.images?.[0]} alt={p.name} className="w-14 h-14 object-cover" />
                    <div>
                      <div className="text-sm font-semibold">{p.name}</div>
                      <div className="sq-eyebrow">{p.id} · {p.category}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
          {results?.orders?.length > 0 && (
            <div>
              <div className="sq-eyebrow mb-3">Orders</div>
              {results.orders.map((o) => (
                <div key={o.id} className="text-sm py-1">{o.id} · ₹{o.total}</div>
              ))}
            </div>
          )}
          {results && !results.products?.length && !results.orders?.length && (
            <div className="text-sm text-[#4B5563]">No results yet — try shirt, linen, navy, or an order ID.</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default function Header() {
  const { user, logout } = useAuth();
  const { count } = useCart() || { count: 0 };
  const [searchOpen, setSearchOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const [hoveredCat, setHoveredCat] = useState(null);
  const [menuCats, setMenuCats] = useState([]);
  const [subsByParent, setSubsByParent] = useState({});
  const accountRef = useRef(null);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/product-categories", { params: { top_level: true, in_menu: true } })
      .then(({ data }) => setMenuCats((data || []).map((c) => ({ key: c.key, label: c.label, route: c.route || `/shop/${c.key}` }))))
      .catch(() => setMenuCats([]));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (menuCats.length === 0) return;
    Promise.all(menuCats.map((c) =>
      api.get("/product-categories", { params: { parent_key: c.key } })
        .then((r) => [c.key, r.data || []])
        .catch(() => [c.key, []])
    )).then((pairs) => setSubsByParent(Object.fromEntries(pairs)));
  }, [menuCats]);

  useEffect(() => {
    if (!accountOpen) return;
    const onDocClick = (e) => {
      if (accountRef.current && !accountRef.current.contains(e.target)) setAccountOpen(false);
    };
    const onEscape = (e) => { if (e.key === "Escape") setAccountOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEscape);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEscape);
    };
  }, [accountOpen]);

  const closeAccount = () => setAccountOpen(false);

  return (
    <>
      <header className="sticky top-0 z-50 bg-white border-b border-black/10" data-testid="site-header">
        <div className="sq-container flex items-center justify-between h-20">
          <Logo />
          <nav className="hidden md:flex items-center gap-8 sq-eyebrow">
            <Link to="/" data-testid="nav-home" className="hover:text-squire-jeans transition-colors flex items-center gap-1"><HomeIcon size={14} /> Home</Link>
            <div
              className="relative"
              onMouseEnter={() => setProductsOpen(true)}
              onMouseLeave={() => { setProductsOpen(false); setHoveredCat(null); }}
            >
              <button type="button" data-testid="nav-products" className="hover:text-squire-jeans transition-colors flex items-center gap-1">
                Products <ChevronDown size={12} className={`transition-transform ${productsOpen ? "rotate-180" : ""}`} />
              </button>
              {productsOpen && menuCats.length > 0 && (
                <div className="absolute left-0 top-full pt-3 z-[70] normal-case tracking-normal flex" data-testid="products-dropdown">
                  <div className="bg-white border border-black/10 shadow-lg w-56">
                    {menuCats.map((c) => (
                      <Link
                        key={c.key}
                        to={c.route}
                        data-testid={`products-dropdown-${c.key}`}
                        onMouseEnter={() => setHoveredCat(c.key)}
                        onClick={() => { setProductsOpen(false); setHoveredCat(null); }}
                        className={`flex items-center justify-between gap-2 px-4 py-3 text-sm hover:bg-black hover:text-white ${hoveredCat === c.key ? "bg-black text-white" : ""}`}
                      >
                        {c.label} <ChevronRight size={14} />
                      </Link>
                    ))}
                  </div>
                  {hoveredCat && subsByParent[hoveredCat]?.length > 0 && (
                    <div className="bg-white border border-black/10 shadow-lg w-56 ml-1" data-testid={`products-dropdown-subs-${hoveredCat}`}>
                      {subsByParent[hoveredCat].map((s) => (
                        <Link
                          key={s.key}
                          to={s.route || `/shop/${hoveredCat}?sub=${s.key}`}
                          data-testid={`products-dropdown-sub-${hoveredCat}-${s.key}`}
                          onClick={() => { setProductsOpen(false); setHoveredCat(null); }}
                          className="block px-4 py-3 text-sm hover:bg-black hover:text-white"
                        >
                          {s.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            <Link to="/fabrics" data-testid="nav-fabrics" className="hover:text-squire-jeans transition-colors">Fabrics</Link>
            <Link to="/bespoke" data-testid="nav-bespoke" className="hover:text-squire-jeans transition-colors">Bespoke Tailoring</Link>
            <Link to="/corporate-gifting" data-testid="nav-gifting" className="hover:text-squire-jeans transition-colors flex items-center gap-1"><Gift size={14} /> Gifting</Link>
            <Link to="/lookbook" data-testid="nav-lookbook" className="hover:text-squire-jeans transition-colors">Lookbook</Link>
            {/* <Link to="/about" data-testid="nav-about" className="hover:text-squire-jeans transition-colors">About</Link> */}
          </nav>
          <div className="flex items-center gap-3">
            <button
              data-testid="master-search-toggle"
              aria-label="search"
              onClick={() => setSearchOpen(true)}
              className="group flex items-center h-10 px-3 border border-black/20 hover:border-squire-jeans transition-all hover:px-4 bg-transparent"
            >
              <Search size={16} />
              <span className="ml-2 sq-eyebrow text-[10px] hidden lg:inline">Search anything</span>
            </button>
            <Link to="/wishlist" data-testid="nav-wishlist" className="relative p-2 hover:text-squire-jeans transition-colors hidden md:inline-flex" aria-label="Wishlist">
              <Heart size={20} />
            </Link>
            <Link to="/cart" data-testid="nav-cart" className="relative p-2 hover:text-squire-jeans transition-colors">
              <ShoppingBag size={20} />
              {count > 0 && <span className="absolute -top-1 -right-1 bg-squire-jeans text-white text-[10px] w-4 h-4 flex items-center justify-center rounded-full">{count}</span>}
            </Link>
            <NotificationBell />
            {user ? (
              <div className="relative" ref={accountRef}>
                <button
                  data-testid="nav-account"
                  onClick={() => setAccountOpen((v) => !v)}
                  aria-expanded={accountOpen}
                  className="p-2 hover:text-black/70 transition-colors flex items-center gap-1"
                >
                  <User size={20} />
                </button>
                {accountOpen && (() => {
                  const role = user.role || "user";
                  const isAdmin = ["super_admin", "major_admin", "admin"].includes(role);
                  const isMaster = role === "master_manager";
                  const isPackaging = role === "packaging_manager";
                  const isStaff = isAdmin || isMaster || isPackaging;
                  const item = (to, label, Icon, testid) => (
                    <Link key={testid} to={to} onClick={closeAccount} data-testid={testid}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-[#374151] hover:bg-squire-jeans hover:text-white transition-colors">
                      <Icon size={16} className="shrink-0 opacity-70" /> {label}
                    </Link>
                  );
                  const customerLinks = [
                    item("/dashboard", "My Dashboard", LayoutDashboard, "dropdown-dashboard"),
                    item("/orders", "My Orders", Package, "dropdown-orders"),
                    item("/wardrobe", "Virtual Wardrobe", Shirt, "dropdown-wardrobe"),
                    item("/event-planner", "Event Planner", CalendarDays, "dropdown-event"),
                    item("/returns", "Returns & Alterations", RotateCcw, "dropdown-returns"),
                    item("/size-finder", "Size Finder", Ruler, "dropdown-size"),
                  ];
                  const staffLinks = [];
                  if (isAdmin) staffLinks.push(item("/admin", "Admin Panel", LayoutDashboard, "dropdown-admin"));
                  if (isMaster) staffLinks.push(item("/master-manager", "Master Manager", ClipboardList, "dropdown-master-manager"));
                  if (isPackaging) staffLinks.push(item("/packaging", "Packaging", Boxes, "dropdown-packaging"));
                  const links = isStaff ? [...staffLinks, item("/orders", "My Orders", Package, "dropdown-orders")] : customerLinks;
                  const roleLabel = role.replace(/_/g, " ");
                  return (
                    <div className="absolute right-0 mt-2 w-60 bg-white border border-black/10 rounded-xl shadow-xl overflow-hidden z-[70] origin-top-right animate-in fade-in-0 zoom-in-95 slide-in-from-top-1 duration-150" data-testid="account-dropdown">
                      <div className="px-4 py-3.5 bg-gradient-to-br from-squire-jeans to-black text-white">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-white/15 grid place-items-center font-display">{(user.name || "U").slice(0, 1).toUpperCase()}</div>
                          <div className="min-w-0">
                            <div className="text-sm font-semibold truncate">{user.name}</div>
                            <div className="text-[11px] text-white/70">{user.id}</div>
                          </div>
                        </div>
                        {isStaff && <span className="inline-block mt-2 text-[9px] uppercase tracking-[0.18em] bg-white/20 px-2 py-0.5 rounded-full capitalize">{roleLabel}</span>}
                      </div>
                      <div className="py-1">{links}</div>
                      <button data-testid="dropdown-logout" onClick={() => { closeAccount(); logout(); nav("/"); }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors border-t border-black/5">
                        <LogOut size={16} className="shrink-0 opacity-70" /> Sign Out
                      </button>
                    </div>
                  );
                })()}
              </div>
            ) : (
              <Link to="/auth" data-testid="nav-login" className="sq-btn-outline !py-2 !px-4 !text-[10px]">Sign In</Link>
            )}
          </div>
        </div>
      </header>
      <MasterSearch open={searchOpen} setOpen={setSearchOpen} />
    </>
  );
}
